import React, { useState, useEffect, useCallback } from "react";
import { Form, Row, Col, Card, Button, Spinner, Alert, Table, Badge } from "react-bootstrap";
import { FaSearch, FaProjectDiagram, FaFileInvoiceDollar, FaLink } from "react-icons/fa";
import firebaseApp from "../../../firebase/credenciales";
import { getFirestore, collection, query, where, getDocs, limit } from "firebase/firestore";
import { toNum } from "../utils";

const db = getFirestore(firebaseApp);

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };

const AnalisisContable = ({ usuario, initialCuo }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [cuo, setCuo] = useState(initialCuo || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [asiento, setAsiento] = useState(null);

  const buscarAsiento = useCallback(async () => {
    if (!rucEmpresa || !cuo) return;
    try {
      setLoading(true);
      setError("");
      setAsiento(null);

      // Intentar buscar por campo 'cuo' o por ID directo
      const q = query(
        collection(db, "gco_asientos"),
        where("ruc_empresa", "==", rucEmpresa),
        where("cuo", "==", cuo.trim())
      );
      const snap = await getDocs(q);
      
      if (!snap.empty) {
        setAsiento({ id: snap.docs[0].id, ...snap.docs[0].data() });
      } else {
        // Fallback: buscar por ID del documento
        const q2 = query(
          collection(db, "gco_asientos"),
          where("ruc_empresa", "==", rucEmpresa),
          where("asiento_id", "==", cuo.trim())
        );
        const snap2 = await getDocs(q2);
        if (!snap2.empty) {
          setAsiento({ id: snap2.docs[0].id, ...snap2.docs[0].data() });
        } else {
          setError(`No se encontró ningún asiento contable con CUO o ID: "${cuo}"`);
        }
      }
    } catch (e) {
      console.error(e);
      setError("Error consultando el ledger central.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa, cuo]);

  useEffect(() => {
    if (initialCuo) {
      setCuo(initialCuo);
    }
  }, [initialCuo]);

  useEffect(() => {
    if (cuo && initialCuo) {
      buscarAsiento();
    }
  }, [buscarAsiento, cuo, initialCuo]);

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}

      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center mb-3">
            <FaSearch className="text-secondary me-2" size={20} />
            <h5 className="mb-0 fw-semibold">Trazabilidad E2E Contable</h5>
          </div>
          <Row className="g-3 align-items-end">
            <Col md={8}>
              <Form.Group>
                <Form.Label className="small fw-semibold text-muted">Ingrese el CUO o Identificador del Asiento</Form.Label>
                <Form.Control type="text" placeholder="Ej: VENTA_1001, CUO-202608-001..." value={cuo} onChange={(e) => setCuo(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4} className="text-end">
              <Button style={BTN} className="rounded-pill w-100 py-2 fw-bold" onClick={buscarAsiento} disabled={loading || !cuo}>
                {loading ? <Spinner animation="border" size="sm" /> : <><FaSearch /> Rastrear Asiento</>}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {asiento && (
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header className="bg-light border-0 py-3 px-4 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <FaProjectDiagram className="text-primary me-2" size={20} />
              <h5 className="mb-0 fw-bold">Detalle y Trazabilidad del Asiento</h5>
            </div>
            <Badge bg="primary" className="px-3 py-2 rounded-pill fw-bold">
              CUO: {asiento.cuo || "—"}
            </Badge>
          </Card.Header>
          <Card.Body className="p-4">
            {/* Trazabilidad Origen */}
            <h6 className="fw-semibold text-muted mb-3">Trazabilidad de Hecho Económico Original</h6>
            <div className="p-3 bg-light rounded-3 mb-4 border d-flex justify-content-between align-items-center">
              <div>
                <div className="small text-muted mb-1">Módulo Origen</div>
                <div className="fw-bold text-dark">{asiento.metadata?.sourceModule || asiento.origen || "Operativo Directo"}</div>
              </div>
              <div>
                <div className="small text-muted mb-1">Tipo de Documento</div>
                <div className="fw-bold text-dark">{asiento.metadata?.sourceType || "Operación Contable"}</div>
              </div>
              <div>
                <div className="small text-muted mb-1">ID Documento Origen</div>
                <div className="fw-bold text-dark">{asiento.metadata?.sourceId || asiento.origenId || "—"}</div>
              </div>
              <div>
                {asiento.origenId && (
                  <Button variant="outline-primary" className="rounded-pill btn-sm fw-bold">
                    <FaLink /> Ver Documento
                  </Button>
                )}
              </div>
            </div>

            <Row className="mb-4">
              <Col md={3}>
                <div className="small text-muted">Fecha de Asiento</div>
                <div className="fw-bold">{asiento.fecha}</div>
              </Col>
              <Col md={6}>
                <div className="small text-muted">Glosa / Concepto</div>
                <div className="fw-bold">{asiento.glosa}</div>
              </Col>
              <Col md={3}>
                <div className="small text-muted">Moneda</div>
                <div className="fw-bold">{asiento.moneda || "PEN"}</div>
              </Col>
            </Row>

            {/* Partida Doble */}
            <h6 className="fw-semibold text-muted mb-3">Entradas Contables (Partida Doble)</h6>
            <Table hover responsive className="mb-0 border rounded-3">
              <thead className="table-light">
                <tr>
                  <th>Cuenta</th>
                  <th>Descripción Cuenta</th>
                  <th className="text-end">Debe</th>
                  <th className="text-end">Haber</th>
                </tr>
              </thead>
              <tbody>
                {(asiento.debe || []).map((d, idx) => (
                  <tr key={`debe-${idx}`}>
                    <td><strong>{d.cuenta}</strong></td>
                    <td className="text-muted">{d.descripcion}</td>
                    <td className="text-end text-success fw-semibold">S/ {toNum(d.monto).toFixed(2)}</td>
                    <td className="text-end text-muted">—</td>
                  </tr>
                ))}
                {(asiento.haber || []).map((h, idx) => (
                  <tr key={`haber-${idx}`}>
                    <td><strong>{h.cuenta}</strong></td>
                    <td className="text-muted">{h.descripcion}</td>
                    <td className="text-end text-muted">—</td>
                    <td className="text-end text-danger fw-semibold">S/ {toNum(h.monto).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="table-light fw-bold">
                  <td colSpan={2} className="text-uppercase text-dark">Suma Total</td>
                  <td className="text-end text-success">S/ {toNum(asiento.total_debe || asiento.debe?.reduce((s, x) => s + toNum(x.monto), 0)).toFixed(2)}</td>
                  <td className="text-end text-danger">S/ {toNum(asiento.total_haber || asiento.haber?.reduce((s, x) => s + toNum(x.monto), 0)).toFixed(2)}</td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default AnalisisContable;
